/**
 * Product Reviews JavaScript
 * Handles API-based review loading with pagination
 */

class ProductReviews {
  constructor() {
    this.currentPage = 0;
    this.itemsPerPage = 3;
    this.totalReviews = 0;
    this.modelId = null;
    this.isLoading = false;
    
    this.init();
  }

  init() {
    // Get model ID from reference element
    this.modelId = this.extractModelId();
    if (!this.modelId) {
      return;
    }

    // Initialize pagination controls
    this.setupPaginationControls();
    
    // Load initial reviews
    this.loadReviews();
  }

  extractModelId() {
    if (window.reviewsTranslations && window.reviewsTranslations.modelCode) {
      return window.reviewsTranslations.modelCode;
    }
    return null;
  }

  setupPaginationControls() {
    const commentsSections = document.querySelectorAll('.ReviewSummary-comments');
    if (!commentsSections.length) return;

    commentsSections.forEach((commentsSection, index) => {
      const paginationId = index === 0 ? 'mobile' : 'desktop';
      const paginationHtml = `
        <div class="review-pagination review-pagination--${paginationId}" style="display: none;">
          <button class="review-pagination__btn review-pagination__prev review-pagination__prev--${paginationId}" disabled>
            <span>${window.reviewsTranslations?.previous || '← Précédent'}</span>
          </button>
          <span class="review-pagination__info review-pagination__info--${paginationId}"></span>
          <button class="review-pagination__btn review-pagination__next review-pagination__next--${paginationId}">
            <span>${window.reviewsTranslations?.next || 'Suivant →'}</span>
          </button>
        </div>
      `;

      commentsSection.insertAdjacentHTML('beforeend', paginationHtml);
    });

    // Add event listeners for both mobile and desktop
    const prevBtns = document.querySelectorAll('.review-pagination__prev');
    const nextBtns = document.querySelectorAll('.review-pagination__next');

    prevBtns.forEach(btn => {
      btn.addEventListener('click', () => this.previousPage());
    });
    nextBtns.forEach(btn => {
      btn.addEventListener('click', () => this.nextPage());
    });
  }

  async loadReviews() {
    if (this.isLoading || !this.modelId) return;

    this.isLoading = true;
    this.showLoadingState();

    try {
      const rangeStart = this.currentPage * this.itemsPerPage;
      const rangeEnd = rangeStart + this.itemsPerPage - 1;
      const apiUrl = window.reviewsApiUrl;
      if (!apiUrl) {
        throw new Error('Reviews API URL is not defined.');
      }
      const url = `${apiUrl}/reviews/model/${this.modelId}/only_reviews?range=${rangeStart}-${rangeEnd}`;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reviews = await response.json();
      
      const translatedReviews = await this.translateReviews(reviews);
      this.renderReviews(translatedReviews);
      this.updatePaginationControls(reviews.length);
      
    } catch (error) {
      this.showError();
    } finally {
      this.isLoading = false;
    }
  }

  async translateReviews(reviews) {
    const locale = window.reviewsTranslations?.locale;
    if (!locale || !reviews || reviews.length === 0) {
      return reviews;
    }

    const apiUrl = window.reviewsApiUrl;
    const translationPromises = reviews.map(async (review) => {
      try {
        const translateUrl = `${apiUrl}/reviews/review/${review.id}/${locale}/translate`;
        const response = await fetch(translateUrl);
        
        if (response.ok) {
          const translatedData = await response.json();
          return {
            ...review,
            title: translatedData.title || review.title,
            message: translatedData.message || review.comment
          };
        }
      } catch (error) {
        console.error(`Failed to translate review ${review.id}:`, error);
      }
      return review;
    });

    return await Promise.all(translationPromises);
  }

  renderReviews(reviews) {
    const reviewLists = document.querySelectorAll('.ReviewList');
    if (!reviewLists.length) return;

    if (!reviews || reviews.length === 0) {
      reviewLists.forEach(reviewList => {
        reviewList.innerHTML = `<p class="no-reviews">${window.reviewsTranslations?.noReviews || 'Aucun avis disponible pour cette page.'}</p>`;
        reviewList.style.minHeight = '';
      });
      return;
    }

    const reviewsHtml = reviews.map(review => this.createReviewHTML(review)).join('');
    
    // Update both mobile and desktop review lists
    reviewLists.forEach(reviewList => {
      reviewList.innerHTML = reviewsHtml;
      reviewList.style.minHeight = '';
    });
  }

  createReviewHTML(review) {
    const rating = review.rating ? review.rating.code : 0;
    const title = this.escapeHtml(review.title || '');
    const comment = this.escapeHtml(review.message || review.comment || '');
    const username = this.escapeHtml(review.author?.username || window.reviewsTranslations?.anonymous || 'Anonyme');
    const date = review.publisherDate ? new Date(review.publisherDate).toLocaleDateString('fr-FR') : '';

    const starsHtml = this.generateStarsHTML(rating);

    return `
      <div class="ReviewItem">
        ${title ? `<h3>${title}</h3>` : ''}
        <p>${comment}</p>
        <div class="ReviewMeta">
          <span>${username}</span>
          <span>|</span>
          <span>${date}</span>
          <span>|</span>
          <span class="StarRating rating__stars">
            ${starsHtml}
          </span>
        </div>
      </div>
    `;
  }

  generateStarsHTML(rating) {
    const maxStars = 5;
    let starsHtml = '';
    
    for (let i = 1; i <= maxStars; i++) {
      if (i <= rating) {
        starsHtml += `<svg fill="none" focusable="false" width="14" height="14" class="icon icon--rating-star rating__star rating__star--full" viewBox="0 0 14 13">
          <path d="M7 0L8.6458 4.73475L13.6574 4.83688L9.66296 7.86525L11.1145 12.6631L7 9.8L2.8855 12.6631L4.33704 7.86525L0.342604 4.83688L5.3542 4.73475L7 0Z" fill="currentColor"></path>
        </svg>`;
      } else {
        starsHtml += `<svg fill="none" focusable="false" width="14" height="14" class="icon icon--rating-star rating__star rating__star--empty" viewBox="0 0 14 13">
          <path d="M7 0L8.6458 4.73475L13.6574 4.83688L9.66296 7.86525L11.1145 12.6631L7 9.8L2.8855 12.6631L4.33704 7.86525L0.342604 4.83688L5.3542 4.73475L7 0Z" fill="currentColor"></path>
        </svg>`;
      }
    }
    
    return starsHtml;
  }

  updatePaginationControls(currentPageCount) {
    const paginations = document.querySelectorAll('.review-pagination');
    const prevBtns = document.querySelectorAll('.review-pagination__prev');
    const nextBtns = document.querySelectorAll('.review-pagination__next');
    const infos = document.querySelectorAll('.review-pagination__info');

    if (!paginations.length) return;

    // Show pagination if we have reviews
    if (currentPageCount > 0) {
      paginations.forEach(pagination => {
        pagination.style.display = 'flex';
      });
    }

    // Update buttons state for all pagination controls
    prevBtns.forEach(btn => {
      btn.disabled = this.currentPage === 0;
    });
    nextBtns.forEach(btn => {
      btn.disabled = currentPageCount < this.itemsPerPage;
    });

    // Update info for all pagination controls
    infos.forEach(info => {
      const pageText = window.reviewsTranslations?.pageInfo?.replace('{{ page }}', this.currentPage + 1) || `Page ${this.currentPage + 1}`;
      info.textContent = pageText;
    });
  }

  previousPage() {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.loadReviews();
    }
  }

  nextPage() {
    this.currentPage++;
    this.loadReviews();
  }

  showLoadingState() {
    const reviewLists = document.querySelectorAll('.ReviewList');
    reviewLists.forEach(reviewList => {
      // Prevent shrinking by setting min-height to current height
      if (reviewList.offsetHeight > 0) {
        reviewList.style.minHeight = `${reviewList.offsetHeight}px`;
      }
      reviewList.innerHTML = `<div class="review-loading">${window.reviewsTranslations?.loading || 'Loading reviews...'}</div>`;
    });
  }

  hideLoadingState() {
    // Loading state is replaced by actual content in renderReviews
  }

  showError() {
    const reviewLists = document.querySelectorAll('.ReviewList');
    reviewLists.forEach(reviewList => {
      reviewList.innerHTML = `<div class="review-error">${window.reviewsTranslations?.error || 'Erreur lors du chargement des avis.'}</div>`;
    });
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize reviews when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  // Only initialize if we're on a product page with reviews
  if (document.querySelector('.ReviewsContainer')) {
    new ProductReviews();
  }
});
